import {ActionIcon, Menu, Text, Tooltip} from '@mantine/core';
import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import type {EntitySearchTab} from './entity-search/EntitySearchModal';

interface AddInputsMenuProps {
  disabled?: boolean;
  // Disables "Enter data" and "Search for entities" at the per-batch input limit. Upload stays
  // enabled so users get an explicit limit error instead of silently truncating data.
  enterDataDisabled?: boolean;
  maxInputs: number;
  onEnterData: () => void;
  // Omitted by hosts that can't search the registry (standalone), which hides the item.
  onSearchEntities?: (tab: EntitySearchTab) => void;
  onUploadFile: () => void;
}

/**
 * The "+" in the Inputs header: add one input by hand, upload a file, or (where the registry is
 * available) pick entities.
 */
export default function AddInputsMenu({
  disabled = false,
  enterDataDisabled = false,
  maxInputs,
  onEnterData,
  onSearchEntities,
  onUploadFile,
}: AddInputsMenuProps): JSX.Element {
  const addInputsButton = (
    <ActionIcon aria-label="Add inputs" disabled={disabled} size="sm" variant="ghost">
      <BenchlingIcon icon="plus" />
    </ActionIcon>
  );

  if (disabled) {
    return (
      <Tooltip label="Select a model first." withinPortal={true}>
        <span>{addInputsButton}</span>
      </Tooltip>
    );
  }

  return (
    <Menu position="bottom-start" withinPortal={true}>
      <Menu.Target>{addInputsButton}</Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          disabled={enterDataDisabled}
          icon={<BenchlingIcon icon="pen" />}
          onClick={() => {
            trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.RUN_CONFIG_INPUT_METHOD_SELECTED_MANUAL);
            onEnterData();
          }}
          rightSection={enterDataDisabled ? <MaxInputsHint maxInputs={maxInputs} /> : undefined}
        >
          Enter data
        </Menu.Item>
        <Menu.Item
          icon={<BenchlingIcon icon="upload" />}
          onClick={() => {
            trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.RUN_CONFIG_UPLOAD_FILE_CLICKED);
            onUploadFile();
          }}
        >
          Upload file
        </Menu.Item>
        {onSearchEntities != null && (
          <>
            <Menu.Divider />
            <Menu.Item
              disabled={enterDataDisabled}
              icon={<BenchlingIcon icon="search" />}
              onClick={() => {
                onSearchEntities('entities');
              }}
              rightSection={enterDataDisabled ? <MaxInputsHint maxInputs={maxInputs} /> : undefined}
            >
              Search for entities
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

function MaxInputsHint({maxInputs}: {maxInputs: number}): JSX.Element {
  return (
    <Text color="dimmed" size="xs">
      Max {maxInputs}
    </Text>
  );
}
